import Immutable from 'immutable';
import queryString from 'query-string';
import StopWatch from 'utils/stopwatch';
import * as videoActions from 'store/video/actions';
import * as helpers from './helpers';

export const ActionTypes = {
    SET_FOCUS: 'SEARCH_SET_FOCUS',
    SET_QUERY: 'SEARCH_SET_QUERY',
    SET_RESULTS: 'SET_RESULTS',
    SET_RESULT_INDEX: 'SET_RESULT_INDEX',
    PREV_RESULT: 'PREV_RESULT',
    NEXT_RESULT: 'NEXT_RESULT',
    SET_SUGGESTION: 'SET_SUGGESTION',
    PREV_SUGGESTION: 'PREV_SUGGESTION',
    NEXT_SUGGESTION: 'NEXT_SUGGESTION',
    CLEAR: 'SEARCH_CLEAR',
    SEARCH_PARTIAL_TRANSCRIPT_TOKEN: 'SEARCH_PARTIAL_TRANSCRIPT_TOKEN',
    SEARCH_EXACT_TRANSCRIPT_TOKEN: 'SEARCH_EXACT_TRANSCRIPT_TOKEN',
    SEARCH_SUGGESTION: 'SEARCH_SUGGESTION',
    SEARCH_ENTITY_FROM_EXPLORE_MENU: 'SEARCH_ENTITY_FROM_EXPLORE_MENU',
    SEARCH_ENTITY_GROUP_FROM_HEATMAP: 'SEARCH_ENTITY_GROUP_FROM_HEATMAP',
    SEARCH_FROM_CONFIG: 'SEARCH_FROM_CONFIG',
};

export const setFocus = (focus) => ({
    type: ActionTypes.SET_FOCUS,
    focus,
});

export const setQuery = (query) => (dispatch, getState) => {
    const stopwatch = new StopWatch(`Suggestions for: ${query}`);
    const {elementList, config} = getState();

    dispatch({
        type: ActionTypes.SET_QUERY,
        suggestions: helpers.getSuggestions(elementList, query),
        query,
    });

    if (query && config.get('debugPerformance')) stopwatch.print();
};

export const setResults = (search) => (dispatch, getState) => {
    const stopwatch = new StopWatch(`Results for: ${search.query}`);
    const {elementList, config} = getState();
    const results = helpers.getResults(elementList, search);

    if (results.size) {
        dispatch({
            type: ActionTypes.SET_RESULTS,
            query: search.query,
            types: search.types,
            partial: Boolean(search.partial),
            results,
        });

        if (config.get('debugPerformance')) stopwatch.print();
    }
};

export const searchPartialWord = (query) => (dispatch) => {
    dispatch(exports.setResults({
        types: Immutable.List(['Transcript']),
        partial: true,
        query,
    }));
};

export const searchExactWord = (query) => (dispatch) => {
    dispatch(exports.setResults({
        types: Immutable.List(['Transcript']),
        partial: false,
        query,
    }));
};

export const searchEntity = (entity) => (dispatch) => {
    dispatch(exports.setResults({
        types: Immutable.List([entity.get('type')]),
        query: entity.get('displayAs'),
    }));
};

export const searchEntityGroup = (entityGroup) => (dispatch) => {
    dispatch(exports.setResults({
        types: entityGroup.get('types'),
        query: entityGroup.get('displayAs'),
    }));
};

export const searchPartialTranscriptToken = (query) => (dispatch) => {
    dispatch({type: ActionTypes.SEARCH_PARTIAL_TRANSCRIPT_TOKEN, query});
    dispatch(exports.searchPartialWord(query));
    dispatch(exports.nextResult());
};

export const searchExactTranscriptToken = (token) => (dispatch, getState) => {
    const query = token.get('displayAs');
    dispatch({type: ActionTypes.SEARCH_EXACT_TRANSCRIPT_TOKEN, token});
    dispatch(exports.searchExactWord(query));
    const results = getState().search.get('results');

    if (!results.size) return false;

    const resultIndex = results.findIndex(result => result.get('startTime') === token.get('startTime'));
    if (resultIndex >= 0) dispatch(exports.setResultIndex(resultIndex));

    return true;
};

export const searchActiveSuggestion = () => (dispatch, getState) => {
    const state = getState();
    const suggestion = state.search.getIn(['suggestions', state.search.get('suggestionIndex')]);
    if (suggestion) {
        dispatch({type: ActionTypes.SEARCH_SUGGESTION, suggestion});
        dispatch(exports.searchEntityGroup(suggestion));
        dispatch(exports.nextResult());
    }
};

export const searchEntityFromExploreMenu = (entity) => (dispatch) => {
    dispatch({type: ActionTypes.SEARCH_ENTITY_FROM_EXPLORE_MENU, entity});
    dispatch(exports.searchEntity(entity));
    dispatch(exports.nextResult());
};

export const searchEntityGroupFromHeatmap = (entityGroup, block) => (dispatch, getState) => {
    dispatch({type: ActionTypes.SEARCH_ENTITY_GROUP_FROM_HEATMAP, entityGroup});
    dispatch(exports.searchEntityGroup(entityGroup));

    let minDelta = Number.MAX_VALUE;
    let resultIndex = -1;
    getState().search.get('results').forEach((result, index) => {
        const delta = Math.abs(block.get('startTime') - result.get('startTime'));
        if (delta < minDelta) {
            minDelta = delta;
            resultIndex = index;
        }
    });
    if (resultIndex >= 0) dispatch(exports.setResultIndex(resultIndex));
};

export const searchFromConfig = () => (dispatch, getState) => {
    const config = getState().config;

    let searchQuery = config.get('searchQuery');
    const searchVariable = config.get('searchVariable');
    if (!searchQuery && searchVariable) { // Give priority to our own encoded search variable
        searchQuery = queryString.parse(window.location.search)[searchVariable];
    }

    if (searchQuery) {
        dispatch({type: ActionTypes.SEARCH_FROM_CONFIG});
        dispatch(exports.setQuery(searchQuery));
        dispatch(exports.setResults({
            query: searchQuery,
            types: config.get('searchTypes'),
            partial: config.get('searchPartial'),
        }));
    }
};

const seekToResult = (dispatch, getState) => {
    const state = getState();
    const result = state.search.getIn(['results', state.search.get('resultIndex')]);
    if (result) dispatch(videoActions.seek(result.get('startTime')));
};

export const setResultIndex = (index) => (dispatch, getState) => {
    dispatch({type: ActionTypes.SET_RESULT_INDEX, index});
    seekToResult(dispatch, getState);
};

export const prevResult = () => (dispatch, getState) => {
    dispatch({type: ActionTypes.PREV_RESULT});
    seekToResult(dispatch, getState);
};

export const nextResult = () => (dispatch, getState) => {
    dispatch({type: ActionTypes.NEXT_RESULT});
    seekToResult(dispatch, getState);
};

export const setSuggestion = (index) => ({
    type: ActionTypes.SET_SUGGESTION,
    index,
});

export const prevSuggestion = () => ({
    type: ActionTypes.PREV_SUGGESTION,
});

export const nextSuggestion = () => ({
    type: ActionTypes.NEXT_SUGGESTION,
});

export const clear = () => ({
    type: ActionTypes.CLEAR,
});



// WEBPACK FOOTER //
// ./scripts/store/search/actions.js